/** @file File tree traversal and search using globs. */
import * as deque from '@codegrade/deque';
import { Maybe } from '@codegrade/maybe';
import { Glob } from '@/models/glob';
/**
 * Breadth-first generator over a tree.
 *
 * This yields a node and allows us to the pass back a boolean via `next` which
 * tells us whether we want to enqueue the children of that node.
 *
 * @param root The root node to start the traversal from.
 * @param adapter The adapter for inspecting nodes of type `T`.
 * @yields The next node in the queue.
 */
export function* walkBfs(root, adapter) {
    const queue = new deque.Deque([[root]]);
    while (queue.length > 0) {
        // The `!` is ok here since we've checked queue size above.
        for (const candidate of queue.shift()) {
            const descend = (yield candidate) ?? true;
            if (descend && adapter.isDir(candidate)) {
                queue.push(adapter.children(candidate));
            }
        }
    }
}
/**
 * Gets the first file inside a directory which passes the given filter.
 *
 * @param dir The directory to search.
 * @param adapter The adapter for inspecting nodes of type `T`.
 * @param hiddenOk Whether to return hidden entries.
 * @param filter Which adapter predicate to use for filtering.
 * @returns The first matching node or Nothing.
 */
function getFirstFileInDir(dir, adapter, hiddenOk, filter) {
    const walker = walkBfs(dir, adapter);
    let step = walker.next();
    while (step.done != null && step.done === false) {
        const el = step.value;
        const isHidden = adapter.name(el).startsWith('.');
        const visible = hiddenOk || !isHidden;
        if (!adapter.isDir(el) && visible && adapter[filter](el)) {
            return Maybe.of(el);
        }
        step = walker.next(visible);
    }
    return Maybe.empty();
}
/**
 *
 * Gets the first file inside a directory which matches the glob pattern.
 *
 * @param glob The glob pattern.
 * @param root The root node of the tree to search.
 * @param starMatchesHidden Whether `*` should match hidden files.
 * @param adapter The adapter for inspecting nodes of type `T`.
 * @param filter Which adapter predicate to use for filtering.
 * @returns The first matching node or Nothing.
 */
function getFirstMatchingFile(glob, root, { starMatchesHidden }, adapter, filter) {
    const walker = walkBfs(root, adapter);
    let step = walker.next();
    while (step.done != null && step.done === false) {
        const candidate = step.value;
        const globLike = adapter.toGlob(candidate);
        const doesGlobMatch = glob.match(globLike, { exact: true, starMatchesHidden });
        let descend = true;
        if (adapter.isDir(candidate)) {
            if (glob.isDirPattern && doesGlobMatch) {
                const first = getFirstFileInDir(candidate, adapter, starMatchesHidden, filter);
                if (first.isJust()) {
                    return first;
                }
            }
            descend = glob.match(globLike, { exact: false, starMatchesHidden });
        }
        else {
            if (adapter[filter](candidate) && doesGlobMatch) {
                return Maybe.of(candidate);
            }
            descend = false;
        }
        step = walker.next(descend);
    }
    return Maybe.empty();
}
/**
 * Find the first file in a tree, trying a passed glob and then a default glob.
 * We do this with and without hidden file matching.
 *
 * @param root The root node of the tree.
 * @param glob The glob to match against.
 * @param adapter The adapter for inspecting nodes of type `T`.
 * @param filter Which adapter predicate to use for filtering. Defaults to 'isEditable'.
 * @returns The first matching file, or Nothing.
 */
export function getFirstFileByGlob(root, glob, adapter, filter = 'isEditable') {
    // Though this could loop four times in the worst case scenario, there BFS
    // does avoid searching on subtrees which don't prefix match the glob. Most
    // of the time, if no glob is give it will be matching on the first
    // non-binary file anyways.
    for (const usedGlob of [glob.addHead(adapter.name(root)), Glob.default()]) {
        for (const starMatchesHidden of [false, true]) {
            const result = getFirstMatchingFile(usedGlob, root, { starMatchesHidden }, adapter, filter);
            if (result.isJust()) {
                return result;
            }
        }
    }
    return Maybe.empty();
}
